import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid2,
  TextareaAutosize,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import PermissionBlockLabel from "../../../components/common/pop-up/PermissionBlockLabel ";
import DispatchOpsTable from "../../../components/projects/dispatch-ops/DispatchOpsTable";
import { SortType } from "../../../../models/SortType";
import AppButton from "../../../components/common/buttons/AppButton";
import AddShipmentPopup from "../../../components/universal-shipment/AddShipmentPopup";
import { ShipmentRequest } from "../../../../models/AddShipment";
import { toJS } from "mobx";

const DispatchOps = () => {
  const { userStore, orderStore, projectInventoryOpsStore, shipmentStore } =
    useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isAddShipmentPopupOpen, setIsAddShipmentPopupOpen] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { "project-id": projectId } = useParams();
  const orderId = Number(projectId);

  const initialParams = {
    page: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sortBy: searchParams.get("sort_by") || "created_date",
    sortType: (searchParams.get("sort_type") as SortType) || SortType.DESC,
    search: searchParams.get("search") || "",
    shipment_type: searchParams.get("shipment_type")
      ? searchParams.get("shipment_type")?.split(",").map(Number)
      : [],
    status: searchParams.get("status")
      ? searchParams.get("status")?.split(",").map(Number)
      : [],
    order_id: searchParams.get("order_id")
      ? searchParams.get("order_id")?.split(",").map(Number)
      : [],
    c_id: searchParams.get("c_id")
      ? searchParams
          .get("c_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":")!;
            return { id: list[0], value: list[1] };
          })
      : [],
    inventory_type: searchParams.get("inventory_type") || "-1",
    project_id: searchParams.get("project_id")
      ? Number(searchParams.get("project_id"))
      : undefined,
    emails: searchParams.get("emails") || "",
  };

  const [params, setParams] = useState(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("emails") || ""
  );

  useEffect(() => {
    const updateQueryParams = () => {
      // Preserve existing URL parameters (like tab_index)
      const currentSearchParams = new URLSearchParams(location.search);

      Object.entries(params).forEach(([key, value]) => {
        if (key === "emails") {
          // Handle emails parameter specially
          if (value && typeof value === "string" && value.trim().length > 0) {
            currentSearchParams.set(key, value.trim());
          } else {
            currentSearchParams.delete(key);
          }
        } else if (key !== "emails") {
          if (value && (!Array.isArray(value) || value.length > 0)) {
            const paramValue = Array.isArray(value)
              ? value.join(",")
              : value.toString();
            currentSearchParams.set(key, paramValue);
          } else {
            // Remove the parameter if it's empty
            currentSearchParams.delete(key);
          }
        }

        if (key === "c_id") {
          const paramValue = params.c_id
            .map((item) => item.id + ":" + item.value)
            .join(",");
          if (paramValue) {
            currentSearchParams.set(key, paramValue);
          } else {
            currentSearchParams.delete(key);
          }
        }
      });

      const queryString = currentSearchParams.toString();
      navigate({ search: queryString }, { replace: true });
    };

    updateQueryParams();
  }, [params, navigate, shipmentStore, location.search]);

  const fetchShipments = async () => {
    params.order_id = [Number(projectId)];
    if (
      orderStore.orderBasicDetails?.inventory_type !== undefined &&
      orderStore.orderBasicDetails?.inventory_type !== null
    ) {
      params.inventory_type =
        orderStore.orderBasicDetails.inventory_type === 1 ? "1" : "2";
    }

    shipmentStore
      .getShipments(params)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        )
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearchSubmit = () => {
    if (searchTerm.trim().length > 0) {
      setParams((prev: any) => ({
        ...prev,
        emails: searchTerm.trim(),
        search: "", // Clear search parameter
        page: 1,
      }));
    } else {
      setSearchTerm("");
      setParams((prev: any) => ({
        ...prev,
        emails: "",
        search: "",
        page: 1,
      }));
    }
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setParams((prev: any) => ({
      ...prev,
      emails: "",
      search: "",
      page: 1,
    }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev: any) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: any) => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  const handleAddShipment = () => {
    setIsAddShipmentPopupOpen(true);
  };

  const handleCloseAddShipmentPopup = () => {
    setIsAddShipmentPopupOpen(false);
  };

  const handleSubmitShipment = (data: ShipmentRequest) => {
    setIsLoading(true);
    shipmentStore
      .createShipment(data)
      .then(() => {
        showSuccess("Shipment created successfully");
        fetchShipments();
        handleCloseAddShipmentPopup();
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message || "Failed to create shipment"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadTemplate = async () => {
    const url =
      "https://procureasystorage.s3.ap-south-1.amazonaws.com/templates/CSV_Shipping_Template.csv";
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "CSV_Shipping_Template.csv";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error: any) {
      console.log("Fetch failed, trying window.open approach:", error);
      window.open(url, "_blank");
    }
  };

  const handleUploadCsv = (order_id: number, file: File) => {
    setIsLoading(true);
    shipmentStore
      .createShipmentThroughCsv(order_id, file)
      .then(() => {
        showSuccess("CSV uploaded successfully");
        fetchShipments();
        handleCloseAddShipmentPopup();
      })
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message || "Failed to upload CSV"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShipmentSuccess = () => {
    fetchShipments();
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {isLoading ? (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1000,
          })}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
            <>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 5,
                  }}
                >
                  <Box>
                    <CustomerHeading
                      label={orderStore.orderBasicDetails?.label || ""}
                      orderId={Number(orderId)}
                      moduleName="Dispatch Ops"
                      brandName={
                        orderStore.orderBasicDetails?.customer?.name || ""
                      }
                      progressValue={
                        projectInventoryOpsStore.inventoryRequestBody
                          .total_progress || 0
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <AppButton
                        variant="secondary"
                        label="Universal Shipments"
                        onClick={() => {
                          navigate("/inventory/shipments");
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <AppButton
                        variant="primaryBlock"
                        label="Add Shipments"
                        onClick={handleAddShipment}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    mt: 3,
                    mb: 3,
                    display: { xs: "block", md: "flex" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <TextareaAutosize
                    placeholder="Copy paste comma separated email ids here. Limit to 200 ids. Eg abc@swageazy.com,123@swageazy.com"
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                    }}
                    value={searchTerm}
                    style={{
                      width: "100%",
                      borderColor: "#D3D3D3",
                      borderRadius: 4,
                      padding: 6,
                      fontSize: "12px",
                      minHeight: "25px",
                    }}
                  ></TextareaAutosize>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <AppButton
                      variant="primaryBlock"
                      label="Search"
                      onClick={handleSearchSubmit}
                      customStyles={{ width: "100px" }}
                    />
                    <AppButton
                      variant="grayscale"
                      label="Clear"
                      onClick={handleSearchClear}
                      customStyles={{ width: "100px" }}
                    />
                  </Box>
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>
              <Box>
                <DispatchOpsTable
                  maxHeight={window.innerHeight - 350}
                  data={shipmentStore.shipments || []}
                  page_no={params.page}
                  limit={params.limit}
                  total_count={shipmentStore.totalCount || 0}
                  sortBy={params.sortBy}
                  sortType={params.sortType}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={(column, sortType) => {
                    setParams((prev: any) => ({
                      ...prev,
                      sortBy: column,
                      sortType: sortType,
                    }));
                  }}
                  minHeight={window.innerHeight - 350}
                />
              </Box>
            </>
          ) : (
            <PermissionBlockLabel />
          )}
        </>
      )}

      {/* Add Shipment Popup */}
      <AddShipmentPopup
        open={isAddShipmentPopupOpen}
        onClose={handleCloseAddShipmentPopup}
        onSubmit={handleSubmitShipment}
        handleDownloadTemplate={handleDownloadTemplate}
        handleUploadCsv={handleUploadCsv}
        onSuccess={handleShipmentSuccess}
        preSelectedOrderId={orderId}
      />
    </Grid2>
  );
};

export default observer(DispatchOps);
